import { Clock, Search, Sparkles, type LucideIcon } from "lucide-react"

import CoverHero from "@/assets/img/aura/cover-hero.webp"
import { Button } from "@/components/ui/button"
import { AppRoutes } from "@/shared/lib/routes"
import { LegalLink } from "@/shared/components/legal-link"
import { usePreloadQuizImages } from "../../hooks/use-preload-quiz-images"
import type { StepComponentProps } from "./step-contract"

const ICONS: Record<string, LucideIcon> = {
  clock: Clock,
  sparkles: Sparkles,
  search: Search,
}

/**
 * Aura Scan cover (Figma Step0): mix-blend hero of the aura portrait, the
 * value-prop title, a dark-surface card holding three benefit tiles + the CTA,
 * and the policy links. All copy comes from config; the CTA calls `onAdvance`.
 */
export function AuraCoverStep({ step, onAdvance }: StepComponentProps) {
  const features = step.features ?? []

  usePreloadQuizImages("aura")

  return (
    <div className="flex flex-1 flex-col bg-dark-base text-purple-light" data-slot="aura-cover-step">
      <div className="relative min-h-[190px] flex-[1_0_0] overflow-hidden">
        <img
          src={CoverHero}
          alt=""
          className="absolute inset-0 size-full object-cover object-center mix-blend-lighten"
        />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-dark-base" />
      </div>

      <div className="relative z-10 -mt-6 flex flex-col gap-4 px-4 pb-safe">
        <div className="space-y-2 text-center">
          <h1 className="font-display text-2xl font-medium text-purple-light">{step.title}</h1>
          {step.subtitle && <p className="text-base text-purple-light/70">{step.subtitle}</p>}
        </div>

        <div className="flex flex-col gap-2 rounded-[24px] bg-dark-surface p-1 pb-2">
          <div className="flex gap-1">
            {features.map((feature) => {
              const Icon = ICONS[feature.icon] ?? Clock
              const parts = feature.label.split(" ")
              const head = parts.slice(0, -1).join(" ")
              const tail = parts[parts.length - 1]
              return (
                <div
                  key={feature.label}
                  className="flex flex-1 flex-col items-center justify-end gap-2 rounded-[20px] bg-purple-light/10 px-2 py-3"
                >
                  <Icon className="size-6 text-main-purple" strokeWidth={1.5} />
                  <span className="text-center text-sm leading-tight text-purple-light">
                    {head && (
                      <>
                        {head}
                        <br />
                      </>
                    )}
                    {tail}
                  </span>
                </div>
              )
            })}
          </div>

          <div className="px-1">
            <Button variant="gradient" size="xl" className="w-full" onClick={onAdvance}>
              {step.cta ?? "Continue"}
            </Button>
          </div>
        </div>

        <div className="flex justify-between px-1 text-center text-xs text-purple-light/40">
          <span className="flex-1">
            <LegalLink to={AppRoutes.PRIVACY}>Privacy Policy</LegalLink>
          </span>
          <span className="flex-1">
            <LegalLink to={AppRoutes.TERMS}>Terms of use</LegalLink>
          </span>
          <span className="flex-1">
            <LegalLink to={AppRoutes.COOKIES}>Cookie Policy</LegalLink>
          </span>
        </div>
      </div>
    </div>
  )
}
